<script setup lang="ts">
import { computed, ref } from "vue";
import { ButtonSeverity, isNullish, TuButton } from "@tinyurldev/tu-ui-vue";

import useNotify from "@Composables/useNotify";
import usePaddle from "@Composables/usePaddle";
import useTranslation from "@Composables/useTranslation";
import PADDLE_TRANSACTION_TYPE from "@Constants/paddle-transaction-type";
import XOverlay from "@Modules/shared/store/XOverlay";
import PriceModel, { PriceData } from "@Modules/shared/Data/Price";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";
import { XOrdersStore } from "@Modules/shoppingcart/Store/XOrdersStore";
import type { CartItem } from "@Modules/shoppingcart/Data/Models/CartItemModel";
import ShoppingCartTablePrice from "@Modules/shoppingcart/components/ShoppingCartTablePrice.vue";
import PopoverOverhauling from "@Popover/PopoverOverhauling.vue";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import { CheckoutResponse } from "@Modules/shoppingcart/Api/OrdersApi";

//
// Setup
const overlay = XOverlay();
const orderStore = XOrdersStore();
const cartStore = XShoppingCartStore();
const { __ } = useTranslation();
const { notifySuccess, notifyError } = useNotify();
const { openPaddleCheckoutByTransaction } = usePaddle();
const { currentOrganization } = useCurrentOrganization();

//
// Data
const removingRowId = ref<string | null>(null);

//
// Computed
const subtotal = computed(() => formatPrice(cartStore.price));
const discount = computed(() => formatPrice(cartStore.discount));
const total = computed(() => formatPrice(cartStore.total));
const itemCountLabel = computed(() => `${cartStore.quantity} ${__("item", { count: cartStore.quantity })}`);
const canPlaceOrder = computed(() => !cartStore.loading && !cartStore.isEmpty && !orderStore.loading);

const savePercentage = computed(() => {
  const { price, total: totalPrice } = cartStore;

  if (price?.amount > 0 && totalPrice) {
    return ((price.amount - totalPrice.amount) / price.amount) * 100;
  }

  return 0;
});

//
// Methods
function formatPrice(price?: PriceData): string {
  return PriceModel(price ?? null).toHuman();
}

async function removeItem(item: CartItem): Promise<void> {
  removingRowId.value = item.rowId;

  const success = await cartStore.removeItem(item.rowId);

  if (success) {
    notifySuccess({ text: "The domain has been removed from your cart." });
  } else {
    notifyError({ text: "Failed to remove the domain from your cart." });
  }

  removingRowId.value = null;
}

async function checkout(instance: string): Promise<CheckoutResponse | null> {
  const isOrganization = !isNullish(currentOrganization.value);

  if (!isOrganization) {
    return await orderStore.checkout(instance);
  }

  if (currentOrganization.value.can("purchase_branded_domains")) {
    return await orderStore.checkoutForOrganization(currentOrganization.value.slug, instance);
  }

  return null;
}

/**
 * Paddle is the only supported payment gateway here. Unlike the legacy cart
 * (shopping-cart.orders.order / shopping-cart.verify) there is no post-checkout
 * navigation yet — teams-native order and verify pages are still missing.
 */
async function placeOrder(): Promise<void> {
  // Missing shopping-cart/update (it helps to keep BE cart sync with FE cart, no weird charge).
  // Check resources/js/components/cart/Cart.vue:checkoutItems
  const instance = cartStore.cart.instance;

  if (!instance) {
    return;
  }

  const checkoutResult = await checkout(instance);

  if (!checkoutResult) {
    notifyError({ text: orderStore.errors.message || __("shopping_cart.checkout_failed") });
    return;
  }

  await cartStore.refreshCart();
  overlay.hide();

  if (checkoutResult.transaction_id) {
    openPaddleCheckoutByTransaction(PADDLE_TRANSACTION_TYPE.DOMAIN_PURCHASE, { ...checkoutResult, organization: currentOrganization.value?.slug });
  } else {
    notifyError({ text: __("shopping_cart.payment_not_supported") });
  }
}

//
// Exposed Test Vars
defineExpose({ removingRowId, canPlaceOrder });
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      {{ __("shopping_cart.title") }}
    </template>

    <div class="shopping-cart-popover">
      <ShoppingCartTablePrice
        class="shopping-cart-popover__table"
        :items="cartStore.items"
        :loading="cartStore.loading"
        :initialized="cartStore.initialized"
        :removing-row-id="removingRowId"
        @remove="removeItem"
      />

      <div class="shopping-cart-popover__summary">
        <div class="shopping-cart-popover__summary-header">
          <span>{{ __("shopping_cart.order_summary") }}</span>
          <span class="_summary_items">{{ itemCountLabel }}</span>
        </div>

        <div class="shopping-cart-popover__summary-row">
          <span>{{ __("shopping_cart.subtotal") }}</span>
          <span class="_subtotal shopping-cart-popover__summary-value">{{ subtotal }}</span>
        </div>

        <div class="shopping-cart-popover__summary-row">
          <span>{{ __("shopping_cart.discount") }}</span>
          <span class="_discount shopping-cart-popover__summary-value">-{{ discount }}</span>
        </div>

        <div
          v-if="savePercentage"
          class="_save_percentage shopping-cart-popover__savings"
        >
          {{ __("shopping_cart.you_save", { percentage: savePercentage.toFixed(2) }) }}
        </div>

        <hr class="shopping-cart-popover__divider">

        <div class="shopping-cart-popover__total-row">
          <span>{{ __("shopping_cart.total") }}</span>
          <span class="_total">{{ total }}</span>
        </div>

        <TuButton
          class="_place_order shopping-cart-popover__place-order"
          :label="__('shopping_cart.place_order')"
          icon-class="fa-solid fa-shield-halved"
          :severity="ButtonSeverity.PRIMARY"
          :disabled="!canPlaceOrder"
          @click="placeOrder"
        />

        <p class="shopping-cart-popover__tax-note">
          {{ __("shopping_cart.tax_note") }}
        </p>
      </div>
    </div>
  </PopoverOverhauling>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as colors;

.shopping-cart-popover {
  display: flex;
  flex-direction: column;
  gap: spacing.$xs;
  height: 100%;

  &__table {
    min-height: 0;
  }

  &__summary {
    margin-top: auto;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: spacing.$xxxs;
    padding: spacing.$xxs;
    background-color: colors.$background-secondary;
  }

  &__summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: typography.$font-weight-bold;
    margin-bottom: spacing.$xxxs;
  }

  &__summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__summary-value {
    font-weight: typography.$font-weight-semi-bold;
  }

  &__savings {
    text-align: right;
    color: colors.$secondary;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__divider {
    width: 100%;
    margin: spacing.$xxxs 0;
  }

  &__total-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: typography.$font-weight-bold;
    font-size: typography.$h4-font-size;
    margin-bottom: spacing.$xxs;
  }

  &__place-order {
    width: 100%;
  }

  &__tax-note {
    text-align: center;
    font-size: typography.$xs-font-size;
    margin: spacing.$xxs 0 0;
  }
}
</style>
